import { BCTreeSelectEntities, ITreeviewItem } from '@bimser/components';
import * as LayoutCreatorEntities from '@bimser/components/dist/BCLayoutCreator/common/entities';
import { ITreeData } from '@bimser/components/dist/BCTreeSelect/entities';
import { FormItemTypes, IDictionary } from '@bimser/core';
import { Map } from 'immutable';
import { IPropertyField, IPropertyFieldType } from '../entities/common';
import { DataSourceParameterValueType } from '../entities/common/Types';
import FormRuntimeItemManager from '../environments/runtime/FormItemManager';

function sortItems(fields: any[]) {
	// alphabetic
	return fields?.sort((a, b) => a.key.localeCompare(b.key)) || [];
}

// FORMULA MANAGER

export function getFieldsDataForFormulaManager(
	controls: IDictionary<LayoutCreatorEntities.ILayoutDataItem>
): ITreeviewItem<any>[] {
	const controlList: ITreeviewItem<any>[] = [];

	Object.keys(controls).forEach(controlName => {
		const c = controls[controlName];

		if (c.type !== FormItemTypes.EControlTypes.Tab) {
			const control = FormRuntimeItemManager.getFormItemInstance(
				c.type as FormItemTypes.EControlTypes
			);
			const propertyFields = control.__getPropertyFields(Map(c.properties));

			const treeItem: ITreeviewItem<any> = {
				isLeaf: false,
				key: c.id,
				text: c.id,
				title: c.id,
				children: getFieldsDataForFormulaManager_childs(c.id, propertyFields)
			};

			controlList.push(treeItem);
		}
	});

	return sortItems(controlList);
}

function getFieldsDataForFormulaManager_childs(
	parentId: string,
	props: IPropertyField[]
): ITreeviewItem<any>[] {
	const children: ITreeviewItem<any>[] = [];

	props.forEach(p => {
		const hasChild = p.items && p.items.length ? true : false;
		const newParentId = parentId + '.' + p.name;
		const controlChild: ITreeviewItem<any> = {
			key: newParentId,
			text: hasChild ? newParentId : `[${newParentId}]`,
			title: hasChild ? newParentId : `[${newParentId}]`,
			children: hasChild ? getFieldsDataForFormulaManager_childs(newParentId, p.items) : [],
			dataRef: {
				...p
			}
		};

		if (!p.hiddenOn || p.hiddenOn.indexOf('formula-manager') === -1) {
			children.push(controlChild);
		}
	});

	return sortItems(children);
}

// FORMULA MANAGER : END

// PROPERTY MAPPING : START

export function convertFieldsForMapping(
	controls: IDictionary<LayoutCreatorEntities.ILayoutDataItem>
) {
	const controlList: BCTreeSelectEntities.ITreeData[] = [];

	Object.keys(controls).forEach(c => {
		const control = FormRuntimeItemManager.getFormItemInstance(
			controls[c].type as FormItemTypes.EControlTypes
		);
		const pFields: IPropertyField[] = control.__getPropertyFields(Map(controls[c].properties));
		controlList.push({
			selectable: false,
			key: controls[c].id,
			title: controls[c].id,
			value: controls[c].id,
			label: controls[c].id,
			disableCheckbox: true,
			children: convertFieldsForMapping_childs(controls[c].id, pFields)
		});
	});

	return sortItems(controlList);
}

function convertFieldsForMapping_childs(
	parentId: string,
	pFields: IPropertyField[]
): BCTreeSelectEntities.ITreeData[] {
	const children: BCTreeSelectEntities.ITreeData[] = [];

	pFields.forEach(property => {
		const newParentId = `${parentId}.${property.name}`;
		const hasNoChild = !property.items?.length;

		if (!property.hiddenOn || property.hiddenOn.indexOf('data-synchronizer') === -1) {
			children.push({
				externalData: property,
				selectable: hasNoChild,
				key: newParentId,
				title: property.name,
				value: newParentId,
				label: property.name,
				disableCheckbox: !hasNoChild,
				children: !hasNoChild
					? convertFieldsForMapping_childs(newParentId, property.items)
					: []
			});
		}
	});

	return sortItems(children);
}

// PROPERTY MAPPING : END

// DATASOURCE PARAMETER : START

export function getFilteredFieldsAsTree(
	controls: IDictionary<LayoutCreatorEntities.ILayoutDataItem>,
	valueType: DataSourceParameterValueType
) {
	const controlList: BCTreeSelectEntities.ITreeData[] = [];

	Object.keys(controls).forEach(c => {
		const control = FormRuntimeItemManager.getFormItemInstance(
			controls[c].type as FormItemTypes.EControlTypes
		);
		const pFields: IPropertyField[] = control.__getPropertyFields(Map(controls[c].properties));

		const treeItem: BCTreeSelectEntities.ITreeData = {
			selectable: false,
			key: controls[c].id,
			title: controls[c].id,
			value: controls[c].id,
			children: getFilteredFieldsAsTree_childs(controls[c].id, pFields, valueType)
		};

		controlList.push(treeItem);
	});

	return sortItems(controlList);
}

function getFilteredFieldsAsTree_childs(
	parentId: string,
	pFields: IPropertyField[],
	valueType: DataSourceParameterValueType
): BCTreeSelectEntities.ITreeData[] {
	const parameterValueType = convertPrimitiveType(valueType);
	const children: BCTreeSelectEntities.ITreeData[] = [];

	pFields.forEach(property => {
		const selectable =
			property.type === 'any' ||
			property.type === parameterValueType ||
			parameterValueType === 'string';
		const canAddItem =
			property?.items?.find(i => i.type === parameterValueType || i.type === 'object') ||
			selectable;

		if (canAddItem) {
			const newParentId = parentId + '.' + property.name;
			const controlChild: BCTreeSelectEntities.ITreeData = {
				selectable: selectable,
				key: newParentId,
				title: property.name,
				value: newParentId,
				children:
					property.items && property.items.length
						? getFilteredFieldsAsTree_childs(newParentId, property.items, valueType)
						: []
			};

			if (!controlChild.selectable && !controlChild.children?.length) {
				return;
			}

			children.push(controlChild);
		}
	});

	return sortItems(children);
}

function convertPrimitiveType(type: DataSourceParameterValueType): IPropertyFieldType {
	switch (type) {
		case 'decimal':
		case 'integer': {
			return 'number';
		}
		case 'boolean': {
			return 'boolean';
		}
		case 'dateTime':
		case 'date': {
			return 'date';
		}
		case 'string': {
			return 'string';
		}
		case 'array': {
			return 'array';
		}
		default:
			console.warn('Type not found...');
	}
}

// DATASOURCE PARAMETER : END

// ACTION BUTTON PARAMETERS : START

export function getFieldAsTreeSelectItem(
	controls: IDictionary<LayoutCreatorEntities.ILayoutDataItem>
): ITreeData[] {
	const controlList: ITreeData[] = [];

	Object.keys(controls).forEach(controlName => {
		const c = controls[controlName];

		if (c.type !== FormItemTypes.EControlTypes.Tab) {
			const control = FormRuntimeItemManager.getFormItemInstance(
				c.type as FormItemTypes.EControlTypes
			);
			const propertyFields = control.__getPropertyFields(Map(c.properties));

			const treeItem: ITreeData = {
				key: c.id,
				title: c.id,
				value: c.id,
				externalData: { type: c.type },
				isLeaf: null,
				label: null,
				selectable: false,
				children: getFieldAsTreeSelectItem_childs(c.id, propertyFields)
			};

			controlList.push(treeItem);
		}
	});

	return sortItems(controlList);
}

function getFieldAsTreeSelectItem_childs(parentId: string, props: IPropertyField[]): ITreeData[] {
	const children: ITreeData[] = [];

	props.forEach(p => {
		const hasChild = p.items && p.items.length ? true : false;
		const newParentId = parentId + '.' + p.name;
		const controlChild: ITreeData = {
			key: newParentId,
			title: newParentId,
			value: newParentId,
			externalData: { ...p },
			isLeaf: null,
			label: null,
			selectable: !hasChild,
			children: hasChild ? getFieldAsTreeSelectItem_childs(newParentId, p.items) : []
		};

		children.push(controlChild);
	});

	return sortItems(children);
}

// ACTION BUTTON PARAMETERS : END
